import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Reveal } from '@/components/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { TryItNowWidget } from '@/components/TryItNowWidget';
import { PhotoComparisonSlider } from '@/components/PhotoComparisonSlider';
import {
  Sparkles,
  Eye,
  Zap,
  CheckCircle2,
  ArrowRight,
  Wand2,
  Clock,
  Crown,
  Camera,
  Home,
  Tv,
} from 'lucide-react';
import reflectionBefore from '@/assets/reflection-before.jpg';
import reflectionAfter from '@/assets/reflection-after.jpg';

const faqs = [
  {
    q: 'What does this tool actually remove?',
    a: 'Photographer reflections and silhouettes that show up in windows, mirrors, glass shower doors, TV screens, picture frames and any shiny surface — plus the photographer\'s shadow on the floor.',
  },
  {
    q: 'Is the reflection removal tool free to try?',
    a: 'Yes — try the free demo on this page (3 photos, no signup). Upgrade for unlimited cleanup across full listings, starting at $15 for a single property kit.',
  },
  {
    q: 'Will it damage the surface or the room behind it?',
    a: 'No. The AI reconstructs the surface naturally — a clean mirror, a dark TV screen, an undistorted window — while keeping the room\'s geometry and lighting intact.',
  },
  {
    q: 'Does it work on big mirror walls and floor-to-ceiling glass?',
    a: 'Yes. It handles wardrobe mirrors, bathroom vanities, glass shower enclosures, TVs in living rooms and bedrooms, and most large reflective surfaces.',
  },
  {
    q: 'Is removing reflections OK for real estate listings?',
    a: 'Yes — you\'re removing the photographer, not changing the property. We always keep the original alongside the cleaned version. Always check your local MLS rules.',
  },
];

export default function RemovePhotographerReflections() {
  const navigate = useNavigate();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'ListBright AI Photographer Reflection Remover',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://listbright.ai/remove-photographer-reflections',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
      description:
        'Free AI tool to remove photographer reflections and shadows from windows, mirrors, TVs and glass in real estate photos.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="Remove Photographer Reflections from Real Estate Photos – AI Tool"
        description="Erase photographer reflections and shadows from windows, mirrors, TVs and glass surfaces in seconds. Free AI demo, no signup required."
        canonical="https://listbright.ai/remove-photographer-reflections"
        breadcrumbs={[{ label: 'Remove Photographer Reflections', path: '/remove-photographer-reflections' }]}
        jsonLd={jsonLd}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-border/40 py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-6rem] left-[-4rem] w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-6xl mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-0 px-4 py-1.5 animate-fade-in">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Free AI Tool
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] animate-fade-up">
                Remove <span className="text-gradient">Photographer Reflections</span> in Seconds
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-up" style={{ animationDelay: '80ms' }}>
                Erase yourself from every mirror, TV and window. Listing photos look like a pro
                shot them — with no awkward silhouette in the glass.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Mirrors, TVs &amp; windows</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Removes floor shadows</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> 30 sec turnaround</span>
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild size="lg" className="group shadow-lg">
                  <a href="#try">Try It Free <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/pricing">See Pricing</Link>
                </Button>
              </div>
            </div>
            <Reveal delay={120} className="relative">
              <div className="absolute -inset-5 bg-gradient-to-tr from-primary/20 via-primary/10 to-transparent blur-3xl rounded-[2.5rem] opacity-70 pointer-events-none" />
              <div className="relative rounded-2xl overflow-hidden shadow-elevated border border-border/50">
                <PhotoComparisonSlider originalSrc={reflectionBefore} enhancedSrc={reflectionAfter} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Try widget ── */}
      <section id="try" className="py-16 md:py-20 border-b border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Remove a Reflection Free</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Upload a photo with you visible in a mirror, TV or window — get a clean version in 30 seconds.
            </p>
          </Reveal>
          <Reveal delay={80}>
            <TryItNowWidget onGetStarted={() => navigate('/auth')} />
          </Reveal>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              <Wand2 className="w-4 h-4 text-primary" /> How it works
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">How Reflection Removal Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Three steps. No software to install. Works on every browser.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Camera, num: '01', title: 'Upload Photo', desc: 'Drop any room shot with reflections in a mirror, TV, window or glass surface.' },
              { icon: Wand2, num: '02', title: 'AI Detection', desc: 'Our model finds the photographer\'s reflection, silhouette and floor shadow.' },
              { icon: Eye, num: '03', title: 'Download Clean Photo', desc: 'Get a clean, reflection-free version in about 30 seconds.' },
            ].map(({ icon: Icon, num, title, desc }, i) => (
              <Reveal key={title} delay={i * 100}>
                <div className="card-premium relative h-full rounded-2xl border border-border/60 bg-card p-7 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-primary/50 tracking-widest">{num}</span>
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Surfaces ── */}
      <section className="py-20 lg:py-28 border-b border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <Badge variant="outline" className="mb-4"><Tv className="w-3.5 h-3.5 mr-1.5" /> Surfaces We Handle</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">Every Shiny Surface, Covered</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Wherever your camera shows up, we make you disappear.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Bathroom Mirrors', desc: 'Vanity mirrors, full-length mirrors, medicine cabinets.' },
              { name: 'Wardrobe Mirrors', desc: 'Sliding closet doors and floor-to-ceiling mirror walls.' },
              { name: 'TV Screens', desc: 'Living-room and bedroom TVs reflecting the photographer.' },
              { name: 'Windows &amp; Glass Doors', desc: 'Daytime reflections in interior and exterior glazing.' },
              { name: 'Glass Shower Enclosures', desc: 'Bathroom shower glass and partitions.' },
              { name: 'Photographer Shadows', desc: 'Silhouettes cast on floors, walls and countertops.' },
            ].map((s, i) => (
              <Reveal key={s.name} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-5">
                  <h3 className="font-semibold mb-1" dangerouslySetInnerHTML={{ __html: s.name }} />
                  <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: s.desc }} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why upgrade ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <Badge className="mb-4"><Crown className="w-3.5 h-3.5 mr-1.5" /> Beyond Reflections</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">The Complete Listing Suite</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Reflection removal is one of many tools included with every listing. Upgrade for the
              full package — photos, descriptions, video, microsite — in about 10 minutes.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Eye, title: 'Photographer Cleanup', desc: 'Erase reflections, silhouettes and shadows across every surface.' },
              { icon: Sparkles, title: '10 Enhancement Rules', desc: 'Brightness, color, sky replace, lawn refresh — applied automatically.' },
              { icon: Home, title: '5 Description Formats', desc: 'Facebook, Instagram, Stories, Marketplace, Premium — generated in parallel.' },
              { icon: Zap, title: '28+ Languages', desc: 'Every description translated and saved in 28 languages at once.' },
              { icon: Camera, title: 'Slideshow & Cinematic Video', desc: 'Listing video built from your cleaned photos automatically.' },
              { icon: Clock, title: 'Done in 10 Minutes', desc: 'A complete, marketplace-ready listing — every time.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-6 space-y-3 group">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center space-y-4">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="text-base shadow-lg">
                <Link to="/single-property-kit">
                  Get Single Property Kit – $15
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/pricing">See All Plans</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Trusted by agents in 30+ countries. Cancel anytime.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold">Frequently Asked Questions</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <div className="text-center mt-12">
            <Button asChild size="lg" className="group shadow-lg">
              <a href="#try">
                Try Reflection Removal Free
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}